import css from './styles/view-as-customer-selector.scss';
import React, { useState } from 'react';
import {
  getIMUser,
  setIMUser,
  getCadenceAccounts,
  getActiveAccountInfo,
  setActiveAccountInfo,
} from '../../redux/actions/appActions';
import { useSelector, useDispatch } from 'react-redux';
import { SecondaryNavigation } from 'shamrock-clover-ui/dist/clover/components/SecondaryNavigation/SecondaryNavigation';
import invoiceManagerService from '../../services/invoice-manager.service';
import YourAccountsTab from './your-accounts-tab';
import ClientAutoSuggestion from './client-autosuggestion';
import { Icon, Snackbar } from 'shamrock-clover-ui';
import FirebaseAnalytics from '../core/analytics';

type ViewAsCustomerSelectorProps = {
  onClose: Function;
  changeAccount: Function;
  onChangesSaved: Function;
};

const ViewAsCustomerSelector: React.FC<ViewAsCustomerSelectorProps> = (props) => {
  const { onClose, changeAccount, onChangesSaved } = props;
  const dispatch = useDispatch();
  const imUser = useSelector(getIMUser);
  const activeAccountInfo = useSelector(getActiveAccountInfo);
  const cadenceAccounts = useSelector(getCadenceAccounts);

  const viewingAs = imUser?.last_active_account;

  let viewingAsString = '';
  if (viewingAs && activeAccountInfo?.client_number) {
    viewingAsString = `${activeAccountInfo.client_number} - ${activeAccountInfo?.name}`;
    if (activeAccountInfo?.mc_number) {
      viewingAsString = viewingAsString + ` (MC # ${activeAccountInfo.mc_number})`;
    }
  }

  const [failedViewAs, setFailedViewAs] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const tabs = [
    {
      title: 'View Customer',
      onClick: () => {
        setSelectedTab(1);
      },
    },
    {
      title: 'Your Accounts',
      onClick: () => {
        setSelectedTab(2);
      },
    },
  ];

  const stopViewAs = async () => {
    const updatedUser = {
      user_id: imUser.user_id,
      active_account: imUser.last_active_account,
      last_active_account: '',
      accounts: [],
    };
    if (updatedUser.active_account === 'noUserAccounts') {
      updatedUser.active_account = '';
    }
    try {
      const apiResponse = await invoiceManagerService.saveIMUser(imUser.user_id, updatedUser, false);
      if (apiResponse?.nModified === 1) {
        dispatch(
          setIMUser({
            ...imUser,
            active_account: updatedUser.active_account,
            last_active_account: updatedUser.last_active_account,
          }),
        );
        dispatch(
          setActiveAccountInfo(cadenceAccounts.find((acct) => acct.client_number === updatedUser.active_account)),
        );
        onClose();
      } else {
        setFailedViewAs(true);
      }
    } catch {
      setFailedViewAs(true);
    }
  };

  const setViewAsClient = async (selection) => {
    if (selection) {
      FirebaseAnalytics.sendEvent(
        FirebaseAnalytics.EVENTS.SELECT,
        FirebaseAnalytics.MODULES.FACTORING,
        FirebaseAnalytics.PAGES.VIEW_CUSTOMER,
      );
      let lastActive = imUser?.last_active_account;
      if (!lastActive) {
        if (!imUser?.accounts[0]) {
          lastActive = 'noUserAccounts';
        } else {
          lastActive = imUser.active_account;
        }
      }
      const updatedIMUser = { ...imUser, active_account: selection.client_number, last_active_account: lastActive };

      const updatedUser = {
        user_id: imUser.user_id,
        active_account: selection.client_number,
        last_active_account: lastActive,
        accounts: [],
      };
      try {
        const apiResponse = await invoiceManagerService.saveIMUser(imUser.user_id, updatedUser, false);
        if (apiResponse?.nModified === 1) {
          dispatch(setIMUser(updatedIMUser));
          dispatch(setActiveAccountInfo(selection));
          onClose();
        } else {
          setFailedViewAs(true);
        }
      } catch {
        setFailedViewAs(true);
      }
    }
  };

  return (
    <div className={css.viewAsCustomerRoot}>
      {imUser?.last_active_account && (
        <button className={css.viewAsCustomerBanner} onClick={stopViewAs}>
          <div className={css.viewAsCustomerBannerText}>Close View Customer</div>
          <div className={css.viewAsCustomerBannerIcon}>
            <Icon size="14" icon="close" color="white" onClick={() => {}} />
          </div>
        </button>
      )}
      <SecondaryNavigation tabs={tabs} width="636px" startingIndex={0} separatorWidth={1} horizontalPadding={26} />
      <div className={css.tabContainer}>
        {selectedTab === 1 && (
          <div className={css.tab1}>
            <ClientAutoSuggestion onOptionSelect={setViewAsClient} selectedValue={viewingAsString} />
          </div>
        )}
        {selectedTab === 2 && (
          <YourAccountsTab changeAccount={changeAccount} onChangesSaved={onChangesSaved} onChangesCancelled={onClose} />
        )}
      </div>
      <div className={css.grayFooter} />
      <Snackbar
        message="Failed to view customer"
        open={failedViewAs}
        variant={'error'}
        buttonTitle={'REFRESH'}
        duration={5000}
        buttonOnClick={() => {
          window.location.reload();
        }}
        onClose={() => setFailedViewAs(false)}
      />
    </div>
  );
};

export default ViewAsCustomerSelector;
